import {type RecipientActivity, RecipientState} from "core/p2p/peerActivity";

/**
 * The live card's recipient list and ring (kitecorp/transferify-ui#3320), React-free so the rules survive any
 * restyle: the order rows sit in, when finished rows fold away, and how the ring above them is drawn. The card
 * hands in `PeerActivity.recipients` and renders whatever comes back.
 */

/** From this many rows, the finished ones fold into one line under the active ones (P8). */
export const FOLD_FINISHED_FROM = 5;

/**
 * Up to this many recipients the ring gives each its own arc. Past it each arc is under 44° of the circle, too short
 * to judge, so the ring becomes one fill for everyone (the prototype's variant A, owner decision 2026-10-06).
 */
export const SEGMENTED_RING_LIMIT = 6;

/**
 * The gap between two arcs, as a share of the turn: about 25 of the ring's 565 units, which leaves a visible gap
 * once both round caps (half the 14-unit stroke each) have taken their share.
 */
export const RING_SEGMENT_GAP = 0.045;

/** Downloading first, then those who only opened the link, then those who finished (P5). */
const STATE_ORDER: Record<RecipientState, number> = {
	[RecipientState.Downloading]: 0,
	[RecipientState.Opened]: 1,
	[RecipientState.Finished]: 2,
};

/** The rows in the order the card lists them; a recipient keeps its number wherever it sits. */
export function orderedRecipients(recipients: readonly RecipientActivity[]): RecipientActivity[] {
	return [...recipients].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.number - b.number);
}

/** How far one recipient has got, 0 to 1: the whole ring once finished, none while it has asked for nothing. */
export function recipientFraction(recipient: RecipientActivity): number {
	if (recipient.state === RecipientState.Finished) {
		return 1;
	}
	return recipient.requestedBytes > 0 ? Math.min(1, recipient.sentBytes / recipient.requestedBytes) : 0;
}

/** The rows on show, and the line the finished ones folded into, if they did. */
export interface RecipientRows {
	rows: RecipientActivity[];
	/** How many finished recipients the fold line stands for, or null when nothing folds. */
	fold: {finished: number} | null;
}

/**
 * From {@link FOLD_FINISHED_FROM} rows, finished recipients fold into one line so the people still downloading stay
 * in view; opening the fold (`expanded`) shows them again, still in list order.
 */
export function recipientRows(ordered: readonly RecipientActivity[], expanded: boolean): RecipientRows {
	const finished = ordered.filter(recipient => recipient.state === RecipientState.Finished).length;
	if (ordered.length < FOLD_FINISHED_FROM || finished === 0) {
		return {rows: [...ordered], fold: null};
	}
	return {
		rows: expanded ? [...ordered] : ordered.filter(recipient => recipient.state !== RecipientState.Finished),
		fold: {finished},
	};
}

export enum RingKind {
	/** One equal arc per recipient (the prototype's variant B). */
	Segmented = "segmented",
	/** One fill for everyone (variant A), past {@link SEGMENTED_RING_LIMIT}. */
	Aggregate = "aggregate",
}

/** One recipient's arc, in turns from 12 o'clock clockwise. */
export interface RingArc {
	start: number;
	length: number;
	/** How much of the arc is filled, from its start. */
	fill: number;
}

export type RecipientsRing = {kind: RingKind.Segmented; arcs: RingArc[]} | {kind: RingKind.Aggregate; fraction: number};

/**
 * The ring above the list. Each recipient gets an equal arc in list order, filled with its own progress, so a
 * newcomer adds an arc without undoing anyone's progress and a finished recipient is a closed arc. Past
 * {@link SEGMENTED_RING_LIMIT} it is one fill: every byte delivered over every byte asked for.
 */
export function recipientsRing(ordered: readonly RecipientActivity[]): RecipientsRing {
	if (ordered.length > SEGMENTED_RING_LIMIT) {
		const requested = ordered.reduce((total, recipient) => total + recipient.requestedBytes, 0);
		const sent = ordered.reduce((total, recipient) => total + recipient.sentBytes, 0);
		return {kind: RingKind.Aggregate, fraction: requested > 0 ? Math.min(1, sent / requested) : 0};
	}
	const share = 1 / ordered.length;
	const length = share - RING_SEGMENT_GAP;
	return {
		kind: RingKind.Segmented,
		arcs: ordered.map((recipient, index) => ({
			start: index * share + RING_SEGMENT_GAP / 2,
			length,
			fill: length * recipientFraction(recipient),
		})),
	};
}

/** What the ring's centre states: how many are downloading, and how fast together. */
export interface RecipientsPace {
	downloading: number;
	/** Their speeds added up; a recipient not yet read adds nothing. They share the sender's upload. */
	bytesPerSecond: number;
}

export function recipientsPace(recipients: readonly RecipientActivity[]): RecipientsPace {
	const active = recipients.filter(recipient => recipient.state === RecipientState.Downloading);
	return {
		downloading: active.length,
		bytesPerSecond: active.reduce((total, recipient) => total + (recipient.bytesPerSecond ?? 0), 0),
	};
}
